<template>
    <div class="form-reports">
        <div class="page-wrapper pt-4">

            <div class="d-flex align-items-center justify-content-between">
                <ExplanationTitle heading="Reports" explanation="Generate field-value breakdown reports from your submissions."></ExplanationTitle>
                <button class="btn btn-primary" @click="openGenerateModal">
                    Generate New Report
                </button>
            </div>

            <!-- Table -->
            <div class="mt-4">
                <div class="table-wrapper">
                    <table class="table reports-table">
                        <thead>
                        <tr>
                            <th>Report Name</th>
                            <th>Field</th>
                            <th>Period</th>
                            <th>Status</th>
                            <th>Generated</th>
                            <th></th>
                        </tr>
                        </thead>
                        <tbody>
                        <tr v-if="!loaded">
                            <td colspan="6">
                                <div class="spinner-border opacity-25"></div>
                            </td>
                        </tr>
                        <tr v-if="loaded && reports.length === 0">
                            <td colspan="6" class="text-center py-5">
                                <h4 class="font-weight-bold">No Reports Yet</h4>
                                <p class="mb-0">Reports let you analyze how field values are distributed across your submissions.</p>
                            </td>
                        </tr>
                        <tr v-for="report in reports" :key="report.hashId">
                            <td>
                                <a href="#" class="report-name-cell" @click.prevent="viewReport(report)" v-if="report.status === 'ready'">{{ report.name }}</a>
                                <strong class="report-name-cell" v-else>{{ report.name }}</strong>
                            </td>
                            <td>
                                <span class="field-badge">{{ report.field }}</span>
                            </td>
                            <td>{{ formatPeriod(report) }}</td>
                            <td>
                                <span :class="statusBadgeClass(report.status)">{{ report.status }}</span>
                            </td>
                            <td>{{ formatDate(report.created_at) }}</td>
                            <td class="td-actions">
                                <div class="actions-row">
                                    <a href="#"
                                       class="action-btn"
                                       v-if="report.status === 'ready'"
                                       @click.prevent="viewReport(report)">
                                        View Report
                                    </a>
                                    <div class="status-text" v-if="report.status === 'pending' || report.status === 'processing'">
                                        <span class="spinner-border spinner-border-sm"></span>
                                        <span class="status-text-label">Processing...</span>
                                    </div>
                                    <span class="status-text text-danger" v-if="report.status === 'failed'">
                                        Failed
                                    </span>
                                    <a href="#"
                                       class="action-btn action-btn-danger"
                                       v-if="isOwner"
                                       @click.prevent="deleteReport(report)">
                                        Delete
                                    </a>
                                </div>
                            </td>
                        </tr>
                        </tbody>
                    </table>
                </div>
            </div>

            <!-- Generate Modal -->
            <Popup :class="{'active': showGenerateModal}">
                <GenerateReportModal
                    ref="generateModal"
                    @created="onReportCreated"
                    @close="closeGenerateModal">
                </GenerateReportModal>
            </Popup>

            <!-- Report Details Modal -->
            <Popup size="large" :class="{'active': showReportModal}">
                <ReportDetailsModal
                    v-if="selectedReport"
                    :report="selectedReport"
                    :formName="formName"
                    @renamed="onReportRenamed"
                    @deleted="onReportDeleted"
                    @close="closeReportModal">
                </ReportDetailsModal>
            </Popup>

        </div>
    </div>
</template>

<script>
import repository from "@/repository/repository";
import ExplanationTitle from "@/components/widgets/ExplanationTitle";
import Popup from "@/components/widgets/Popup";
import GenerateReportModal from "@/components/forms/GenerateReportModal";
import ReportDetailsModal from "@/components/forms/ReportDetailsModal";

export default {
    name: "FormReports",
    components: { ExplanationTitle, Popup, GenerateReportModal, ReportDetailsModal },
    props: ['formLink', 'formName'],
    data() {
        return {
            reports: [],
            loaded: false,
            showGenerateModal: false,
            showReportModal: false,
            selectedReport: null,
            pollingInterval: null,
        }
    },
    created() {
        this.loadReports();
        this._onPopstate = () => {
            if (this.showReportModal) {
                this.showReportModal = false;
                this.selectedReport = null;
                document.documentElement.style.overflow = "auto";
            } else if (this.showGenerateModal) {
                this.showGenerateModal = false;
                document.documentElement.style.overflow = "auto";
                if (this.$refs.generateModal) this.$refs.generateModal.reset();
            }
        };
        window.addEventListener('popstate', this._onPopstate);
    },
    beforeDestroy() {
        this.stopPolling();
        window.removeEventListener('popstate', this._onPopstate);
    },
    methods: {
        loadReports() {
            if (!this.projectId) return;
            repository.get("/projects/" + this.projectId + "/forms/" + this.formId + "/reports")
                .then(response => {
                    this.reports = response.data;
                    this.loaded = true;
                    this.checkPolling();
                })
                .catch(() => {
                    this.loaded = true;
                })
        },
        checkPolling() {
            var hasPending = false;
            for (var i = 0; i < this.reports.length; i++) {
                if (this.reports[i].status === 'pending' || this.reports[i].status === 'processing') {
                    hasPending = true;
                    break;
                }
            }
            if (hasPending && !this.pollingInterval) {
                this.pollingInterval = setInterval(() => {
                    this.loadReports();
                }, 5000);
            } else if (!hasPending && this.pollingInterval) {
                this.stopPolling();
            }
        },
        stopPolling() {
            if (this.pollingInterval) {
                clearInterval(this.pollingInterval);
                this.pollingInterval = null;
            }
        },
        openGenerateModal() {
            history.pushState({page: 1}, "", this.$route.path);
            this.showGenerateModal = true;
            document.documentElement.style.overflow = "hidden";
        },
        closeGenerateModal() {
            if (this.showGenerateModal) {
                history.back();
            }
        },
        onReportCreated() {
            this.loadReports();
        },
        viewReport(report) {
            repository.get("/reports/" + report.hashId)
                .then(response => {
                    this.selectedReport = response.data;
                    history.pushState({page: 1}, "", this.$route.path);
                    this.showReportModal = true;
                    document.documentElement.style.overflow = "hidden";
                })
                .catch(() => {
                    // fallback
                })
        },
        closeReportModal() {
            if (this.showReportModal) {
                history.back();
            }
        },
        onReportRenamed(newName) {
            if (this.selectedReport) {
                this.selectedReport.name = newName;
            }
            this.loadReports();
        },
        onReportDeleted() {
            this.loadReports();
        },
        deleteReport(report) {
            this.$confirm({
                title: "Delete report?",
                message: "Are you sure you want to delete \"" + report.name + "\"? This action cannot be undone.",
                button: {
                    yes: "Yes, delete",
                    no: "No, cancel"
                },
                callback: confirm => {
                    if (confirm) {
                        repository.delete("/reports/" + report.hashId)
                            .then(() => {
                                this.loadReports();
                            })
                    }
                }
            });
        },
        formatDate(date) {
            if (!date) return '';
            var d = new Date(date);
            return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
        },
        formatPeriod(report) {
            if (report.period === 'all') return 'All time';
            if (report.period === 'monthly' && report.period_month) {
                var parts = report.period_month.split('-');
                var d = new Date(parts[0], parts[1] - 1);
                return d.toLocaleString('default', { month: 'long', year: 'numeric' });
            }
            if (report.period === 'annually' && report.period_year) return report.period_year;
            if (report.period === 'custom') return 'Custom range';
            return report.period;
        },
        statusBadgeClass(status) {
            return {
                'status-badge': true,
                'status-pending': status === 'pending' || status === 'processing',
                'status-ready': status === 'ready',
                'status-failed': status === 'failed',
            };
        },
    },
    computed: {
        formId() {
            return this.$route.params.id;
        },
        projectId() {
            return this.$store.getters.currentProject.hashId;
        },
        isOwner() {
            return this.$store.getters.isOwner;
        },
    },
    watch: {
        formId() {
            this.loaded = false;
            this.loadReports();
        },
        projectId(newValue, oldValue) {
            if (newValue !== null && oldValue === null) {
                this.loadReports();
            } else {
                if (this.$route.path !== "/") this.$router.replace("/");
            }
        },
    },
}
</script>

<style lang="scss" scoped>
@import "../../scss/variables";

.form-reports {
    flex-grow: 1;
    min-height: calc(100svh - 44px - 2rem);
}

.table-wrapper {
    @include tablet {
        overflow: scroll;
        white-space: nowrap;
    }

    @include smartphone {
        overflow: scroll;
        white-space: nowrap;
    }
}

.table.reports-table {
    margin-bottom: 0;

    thead {
        tr {
            th {
                padding: 0 1rem 1.5rem 0;
                color: $dark;
                border-top: none;
                border-bottom: none;
                font-size: 1rem;
                font-weight: 600;
            }
        }
    }

    tbody {
        tr {
            td {
                padding: 1.25rem 1rem 1.25rem 0;
                vertical-align: middle;

                .report-name-cell {
                    font-weight: 600;
                    white-space: nowrap;
                    color: $dark;
                    text-decoration: none;

                    &:hover {
                        color: $primary;
                        text-decoration: underline;
                    }
                }

                .field-badge {
                    display: inline-block;
                    font-size: 0.85rem;
                    font-weight: 500;
                    padding: 0.3rem 0.6rem;
                    border-radius: $box-border-radius;
                    background: rgba($primary, 0.1);
                    color: $primary;
                }

                &.td-actions {
                    text-align: right;

                    .actions-row {
                        display: flex;
                        justify-content: flex-end;
                        align-items: center;
                        white-space: nowrap;
                        flex-wrap: nowrap;
                    }
                }

                .action-btn {
                    display: inline-block;
                    padding: 0.4rem 0.75rem;
                    background: $background-grey;
                    border-radius: $box-border-radius;
                    border: 1px solid transparent;
                    text-decoration: none;
                    color: $dark;
                    font-size: 0.85rem;
                    font-weight: 500;
                    margin-right: 0.25rem;
                    white-space: nowrap;
                    @extend .animated;

                    &:hover {
                        background: lighten($primary, 50%);
                        color: $primary;
                    }

                    &.action-btn-danger {
                        background: rgba($danger, 0.1);
                        color: $danger;
                        margin-right: 0;

                        &:hover {
                            background: $danger;
                            color: $white;
                        }
                    }
                }

                .status-text {
                    font-size: 0.85rem;
                    color: $grey-text;
                    display: inline-flex;
                    align-items: center;
                    margin-right: 0.5rem;
                    line-height: 1;

                    .spinner-border-sm {
                        width: 0.8rem;
                        height: 0.8rem;
                        border-width: 2px;
                        flex-shrink: 0;
                        display: block;
                        margin: 0 0.25rem 0 0;
                    }

                    .status-text-label {
                        display: block;
                    }
                }

                .spinner-border {
                    width: 1.5rem;
                    height: 1.5rem;
                    border-color: $border-dark-grey;
                    border-right-color: transparent;
                    border-width: 2px;
                    margin-top: 0.5rem;
                }
            }
        }
    }
}

.status-badge {
    font-size: 0.7rem;
    font-weight: 600;
    padding: 2px 8px;
    border-radius: 10px;
    text-transform: uppercase;
    letter-spacing: 0.5px;
    white-space: nowrap;

    &.status-pending {
        background: #FEF3C7;
        color: #92400E;
    }

    &.status-ready {
        background: #D1FAE5;
        color: #065F46;
    }

    &.status-failed {
        background: #FEE2E2;
        color: #991B1B;
    }
}
</style>
